import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Button, InputNumber, Space, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface YAxisControlData {
    yAxisMin?: number;
    yAxisMax?: number;
    dataMin?: number;
    dataMax?: number;
    defaultMin: number;
    defaultMax: number;
}

export interface YAxisControlPopoverProps {
    /** Unique identifier for this chart (used for state management) */
    id: string | number;
    /** Current y-axis limits */
    limits: { min: number | null; max: number | null };
    /** Data range information */
    dataRange: YAxisControlData;
    /** Callback when limits are applied */
    onApply: (min: number | null, max: number | null) => void;
    /** Callback when reset is clicked */
    onReset: () => void;
}

export const YAxisControlPopover: React.FC<YAxisControlPopoverProps> = ({
    id,
    limits,
    dataRange,
    onApply,
    onReset,
}) => {
    const { t } = useTranslation();
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [tempYAxisMin, setTempYAxisMin] = useState<number | null>(null);
    const [tempYAxisMax, setTempYAxisMax] = useState<number | null>(null);

    const handlePopoverOpenChange = (visible: boolean) => {
        setPopoverVisible(visible);
        if (visible) {
            setTempYAxisMin(limits.min);
            setTempYAxisMax(limits.max);
        }
    };

    const handleApply = () => {
        const tempMin: number | null = tempYAxisMin !== null ? tempYAxisMin : null;
        const tempMax: number | null = tempYAxisMax !== null ? tempYAxisMax : null;
        if (tempMin !== null && tempMax !== null && tempMin >= tempMax) {
            return;
        }
        onApply(tempMin, tempMax);
        setPopoverVisible(false);
    };

    const handleReset = () => {
        onReset();
        setTempYAxisMin(dataRange.defaultMin);
        setTempYAxisMax(dataRange.defaultMax);
        setPopoverVisible(false);
    };

    const currentMin = tempYAxisMin !== null ? tempYAxisMin : limits.min;
    const currentMax = tempYAxisMax !== null ? tempYAxisMax : limits.max;
    const range = dataRange.defaultMax - dataRange.defaultMin;
    const step = range * 0.05 || 0.1;
    const hasError = currentMin !== null && currentMax !== null && currentMin >= currentMax;

    return (
        <Popover
            content={
                <div style={{ width: 280 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                            <Text strong>{t('shared.yAxisControl.minimumLabel')}</Text>
                            <InputNumber
                                style={{ width: '100%', marginTop: 4 }}
                                value={currentMin}
                                onChange={(val) => setTempYAxisMin(val !== null ? val : null)}
                                step={step}
                                precision={4}
                                placeholder={t('shared.yAxisControl.defaultPlaceholder', { value: dataRange.defaultMin.toFixed(4) })}
                            />
                            {dataRange.dataMin !== undefined && (
                                <Text type="secondary" style={{ fontSize: '11px', marginTop: 2, display: 'block' }}>
                                    {t('shared.yAxisControl.dataMinInfo', { dataMin: dataRange.dataMin.toFixed(4), defaultMin: dataRange.defaultMin.toFixed(4) })}
                                </Text>
                            )}
                        </div>
                        <div>
                            <Text strong>{t('shared.yAxisControl.maximumLabel')}</Text>
                            <InputNumber
                                style={{ width: '100%', marginTop: 4 }}
                                value={currentMax}
                                onChange={(val) => setTempYAxisMax(val !== null ? val : null)}
                                step={step}
                                precision={4}
                                placeholder={t('shared.yAxisControl.defaultPlaceholder', { value: dataRange.defaultMax.toFixed(4) })}
                            />
                            {dataRange.dataMax !== undefined && (
                                <Text type="secondary" style={{ fontSize: '11px', marginTop: 2, display: 'block' }}>
                                    {t('shared.yAxisControl.dataMaxInfo', { dataMax: dataRange.dataMax.toFixed(4), defaultMax: dataRange.defaultMax.toFixed(4) })}
                                </Text>
                            )}
                        </div>
                        {hasError && (
                            <Text type="danger" style={{ fontSize: '11px' }}>
                                {t('shared.yAxisControl.validationError')}
                            </Text>
                        )}
                        <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}>
                            <Button size="small" onClick={handleReset}>
                                {t('general.reset')}
                            </Button>
                            <Button
                                type="primary"
                                size="small"
                                onClick={handleApply}
                                disabled={hasError}
                            >
                                {t('general.apply')}
                            </Button>
                        </Space>
                    </Space>
                </div>
            }
            title={t('shared.yAxisControl.popoverTitle')}
            trigger="click"
            open={popoverVisible}
            onOpenChange={handlePopoverOpenChange}
            placement="bottomRight"
        >
            <Button
                size="small"
                icon={<SettingOutlined />}
                title={t('shared.yAxisControl.configureTooltip')}
            >
                {t('shared.yAxisControl.triggerLabel')}
            </Button>
        </Popover>
    );
};
